import React, { useState, useEffect } from "react";
import { Layout, ConfigProvider, Button, Tooltip, Drawer } from "antd";
import {
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
} from "@ant-design/icons";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { LogoRevi } from "../../assets";
import { Chatbot } from "../../components/Chatbot/Chatbot";
import {
  AdminMenu,
  Logout,
  LanguageSelector,
} from "../../components/Admin/AdminLayout";
import { useShellEstrecho } from "../../hooks/useIsNarrow";

import "../../scss/responsive-portales.scss";
import "./AdminLayout.scss";

const { Header, Sider, Content } = Layout;

// Colores base (encajan con REVI)
const reviShellTheme = {
  token: {
    colorPrimary: "#2088A0",
    colorInfo: "#2088A0",
    colorLink: "#2088A0",
    borderRadius: 14,
    colorBgLayout: "#F6FAFB",
    colorBgContainer: "#FFFFFF",
  },
};

/**
 * La palanca tactil, SOLO por debajo de 992 px. Resuelve dos cosas a la vez sin
 * editar ni un componente de negocio:
 *
 *  - 16 px es el umbral por debajo del cual Safari en iOS amplia la pagina al
 *    enfocar un campo, y no lo deshace al salir. El defecto de antd es 14.
 *  - 44 px es el objetivo tactil de WCAG 2.5.5. El defecto de antd es 32.
 *
 * Los tokens que hay que clavar a mano, porque antd los DERIVA y la derivacion
 * rompe cosas:
 *
 *  - `controlHeightLG` sale de multiplicar por 1,25, o sea 55 px. Se clava en
 *    44 para que un `size="large"` suelto no se coma 11 px de pantalla.
 *  - `Menu.iconSize` e `iconMarginInlineEnd` salen de la fuente, asi que el
 *    hueco del icono subiria de 10 a 17 px y la etiqueta del menu perderia 9
 *    px justo donde el frances va mas justo.
 *  - `controlInteractiveSize` lo lee el Checkbox, y hay uno en los
 *    cuestionarios clinicos que pasaria de 16 a 22 px. El circulo del Radio, en
 *    cambio, mide `fontSizeLG`, que ya esta clavado en 16.
 */
const TOKENS_ESTRECHO = {
  fontSize: 16,
  fontSizeLG: 16,
  fontSizeSM: 13,
  controlHeight: 44,
  controlHeightLG: 44,
  controlInteractiveSize: 16,
  fontSizeHeading1: 22,
  fontSizeHeading2: 22,
  fontSizeHeading3: 22,
  fontSizeHeading4: 18,
  fontSizeHeading5: 16,
  lineHeightHeading1: 1.25,
  lineHeightHeading2: 1.25,
  lineHeightHeading3: 1.25,
  lineHeightHeading4: 1.3,
  lineHeightHeading5: 1.45,
};

// La sesion activa, por ruta.
const SESION_ACTIVA = /\/terapia\/sesion\//;

const COMPONENTES_ESTRECHO = {
  Menu: { fontSize: 14, iconSize: 14, iconMarginInlineEnd: 10 },
};

export function AdminLayout({ children }) {
  const [collapsed, setCollapsed] = useState(false);
  const [menuAbierto, setMenuAbierto] = useState(false);
  const [asistenteAbierto, setAsistenteAbierto] = useState(false);
  const shellEstrecho = useShellEstrecho();
  const { pathname } = useLocation();
  const { t } = useTranslation("common");

  // El cajon se cierra al navegar. Sin esto el terapeuta toca una entrada y se
  // queda mirando el menu, sin entender que la pagina ya ha cambiado detras.
  useEffect(() => {
    setMenuAbierto(false);
  }, [pathname]);

  // La sesion activa se opera de pie, con el paciente poniendose las gafas: es
  // la unica pantalla donde el asistente no se ofrece.
  const enSesion = SESION_ACTIVA.test(pathname);

  const reviLogoSrc = LogoRevi?.logoRevi;

  const tema = shellEstrecho
    ? {
        ...reviShellTheme,
        token: { ...reviShellTheme.token, ...TOKENS_ESTRECHO },
        components: COMPONENTES_ESTRECHO,
      }
    : reviShellTheme;

  const abrirAsistente = () => {
    setMenuAbierto(false);
    setAsistenteAbierto(true);
  };

  return (
    // Sin `componentSize`: con controlHeight en 44 los controles ya miden 44.
    // "large" los llevaria a controlHeightLG.
    <ConfigProvider theme={tema}>
      <div
        className={`admin-layout ${shellEstrecho ? "admin-layout--estrecho" : ""}`}
      >
        {/*
          `hasSider` explicito: no dependemos de que antd inspeccione children.
          Y el Sider NO se renderiza en estrecho, no se oculta: la clase
          `ant-layout-has-sider` la gestiona un registro en JS, no el CSS, y con
          ella viene el `width: 0` que aplasta el contenido hasta dejar el
          titulo pintado letra a letra en vertical.
        */}
        <Layout className="admin-layout__layout" hasSider={!shellEstrecho}>
          {!shellEstrecho && (
            <Sider
              className="admin-layout__left"
              width={260}
              collapsedWidth={84}
              collapsed={collapsed}
              trigger={null}
              breakpoint="lg"
            >
              {/* ✅ SOLO LOGO REVI, centrado, ancho completo */}
              <div
                className="admin-layout__brand"
                role="banner"
                aria-label="REVI"
              >
                {reviLogoSrc ? (
                  <img
                    src={reviLogoSrc}
                    alt="REVI"
                    className="admin-layout__brandLogo"
                    loading="lazy"
                  />
                ) : (
                  <div
                    className="admin-layout__brandFallback"
                    aria-hidden="true"
                  >
                    REVI
                  </div>
                )}
              </div>

              <div className="admin-layout__menuWrap">
                <AdminMenu collapsed={collapsed} />
              </div>

              <div className="admin-layout__siderFooter">
                <Tooltip
                  title={collapsed ? "Expandir menú" : "Colapsar menú"}
                  placement="right"
                >
                  <Button
                    type="text"
                    className="admin-layout__collapseBtn"
                    icon={
                      collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />
                    }
                    onClick={() => setCollapsed((v) => !v)}
                    aria-label={collapsed ? "Expandir menú" : "Colapsar menú"}
                  />
                </Tooltip>
              </div>
            </Sider>
          )}

          <Layout className="admin-layout__right">
            <Header className="admin-layout__right-header">
              <div className="admin-layout__right-headerInner">
                {shellEstrecho && (
                  <Button
                    type="text"
                    className="admin-layout__burger"
                    icon={<MenuOutlined />}
                    onClick={() => setMenuAbierto(true)}
                    aria-label={t("nav.openMenu")}
                    aria-expanded={menuAbierto}
                    aria-controls="admin-menu-drawer"
                  />
                )}
                <div className="admin-layout__headerSpacer" />
                <div className="admin-layout__headerActions">
                  {/* El idioma sale de la barra en estrecho y baja al pie del
                      cajon: es lo que hace que quepan hamburguesa y salir. */}
                  {!shellEstrecho && <LanguageSelector />}
                  <Logout />
                </div>
              </div>
            </Header>

            <Content className="admin-layout__right-content">
              {children}
            </Content>
          </Layout>
        </Layout>

        {/* Un solo <AdminMenu>: mismo mapa de rutas y misma seleccion. Lo unico
            que cambia es el contenedor. */}
        {shellEstrecho && (
          <Drawer
            id="admin-menu-drawer"
            placement="left"
            open={menuAbierto}
            onClose={() => setMenuAbierto(false)}
            size={320}
            closable={false}
            rootClassName="admin-layout__drawer"
            styles={{ body: { padding: 0 } }}
          >
            <div className="admin-layout__menuWrap">
              <AdminMenu
                collapsed={false}
                onAsistente={enSesion ? undefined : abrirAsistente}
              />
            </div>
            <div className="admin-layout__drawerFooter">
              <LanguageSelector />
            </div>
          </Drawer>
        )}

        <Chatbot
          mostrarBoton={!shellEstrecho}
          open={shellEstrecho ? asistenteAbierto : undefined}
          onOpenChange={shellEstrecho ? setAsistenteAbierto : undefined}
        />
      </div>
    </ConfigProvider>
  );
}
